import React from "react";
import { motion } from "framer-motion";
import { Check, ArrowRight } from "lucide-react";
import { GUIDANCE, waLink } from "../data/content";
import { Reveal, Eyebrow, EASE } from "./Reveal";

const Row = ({ item, index }) => {
  const flipped = index % 2 === 1;
  return (
    <div
      className="grid md:grid-cols-2 gap-8 md:gap-16 items-center"
      data-testid={`guidance-row-${item.id}`}
    >
      {/* Image */}
      <motion.div
        initial={{ opacity: 0, x: flipped ? 50 : -50 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 1, ease: EASE }}
        className={`relative ${flipped ? "md:order-2" : ""}`}
      >
        <div className="absolute -inset-3 arch border border-[#CBA135]/30 -z-0 hidden sm:block" />
        <div className="relative overflow-hidden arch rounded-b-3xl aspect-[4/5] gold-glow group">
          <img
            src={item.img}
            alt={item.title}
            className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#21070D]/60 via-transparent to-transparent" />
          <span className="absolute top-5 left-5 text-xs uppercase tracking-[0.3em] text-[#EBD08A] bg-[#21070D]/50 backdrop-blur px-4 py-2 rounded-full border border-[#EBD08A]/25">
            {item.kicker}
          </span>
        </div>
      </motion.div>

      {/* Text */}
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 1, ease: EASE, delay: 0.1 }}
        className={flipped ? "md:order-1" : ""}
      >
        <h3 className="font-serif font-medium text-[#21070D] text-3xl sm:text-4xl lg:text-5xl leading-[1.05] tracking-tight">
          {item.title}
        </h3>
        <p className="mt-5 text-[#3A1219] text-lg sm:text-xl leading-relaxed body-serif">
          {item.desc}
        </p>
        <ul className="mt-7 space-y-3 body-serif">
          {item.points.map((p, i) => (
            <li key={i} className="flex items-start gap-3 text-[#3F101A]">
              <span className="mt-0.5 grid place-items-center h-6 w-6 shrink-0 rounded-full bg-[#CBA135]/15 text-[#9C7526]">
                <Check size={14} />
              </span>
              <span className="text-base sm:text-lg">{p}</span>
            </li>
          ))}
        </ul>
        <a
          href={waLink(`Namaste Shreya, I'd like to book the ${item.title} session.`)}
          target="_blank"
          rel="noreferrer"
          data-testid={`guidance-cta-${item.id}`}
          className="group mt-9 inline-flex items-center gap-3 text-sm uppercase tracking-[0.2em] font-semibold text-[#3F101A]"
        >
          <span className="border-b border-[#CBA135] pb-1 group-hover:text-[#9C7526] transition-colors">
            {item.cta}
          </span>
          <ArrowRight
            size={18}
            className="transition-transform duration-400 group-hover:translate-x-1 text-[#9C7526]"
          />
        </a>
      </motion.div>
    </div>
  );
};

export const GuidanceSessions = () => (
  <section
    id="guidance"
    className="relative bg-[#F4EBD9] py-24 sm:py-36 px-6 sm:px-10 md:px-16"
    data-testid="guidance"
  >
    <div className="max-w-6xl mx-auto">
      <div className="max-w-2xl">
        <Reveal>
          <Eyebrow className="text-[#9C7526]">Sessions</Eyebrow>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-5 font-serif font-medium text-[#21070D] text-4xl sm:text-5xl lg:text-6xl tracking-tight">
            Choose the guidance you need right now
          </h2>
        </Reveal>
      </div>

      <div className="mt-16 sm:mt-24 space-y-24 sm:space-y-32">
        {GUIDANCE.map((item, i) => (
          <Row key={item.id} item={item} index={i} />
        ))}
      </div>
    </div>
  </section>
);
